import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Check, Sparkles, Package, ArrowRight } from 'lucide-react';

const plans = [
  {
    name: 'Start',
    description: 'Pro menší weby',
    price: '4 900',
    period: 'jednorázově',
    items: 'do 50 produktů/služeb',
    features: [
      'AI Visibility Audit',
      'Analýza 5 klíčových stránek',
      'Optimalizace obsahu',
      'Technický report',
      'Doporučení pro růst',
    ],
    cta: 'Objednat audit',
    ctaLink: '/audit?package=start',
    popular: false,
    color: '#00A8E8',
  },
  {
    name: 'Růst',
    description: 'Pro e-shopy a větší weby',
    price: '9 900',
    period: 'měsíčně',
    items: '50-500 produktů',
    features: [
      'Vše z Start balíčku',
      'Optimalizace do 20 stránek/měsíc',
      'Monitoring AI změn',
      'Měsíční reporting',
      'Prioritní podpora',
    ],
    cta: 'Začít růst',
    ctaLink: '/audit?package=rust',
    popular: true,
    color: '#00D4AA',
  },
  {
    name: 'Premium',
    description: 'Pro velké e-shopy',
    price: '24 900',
    period: 'měsíčně',
    items: '500+ produktů',
    features: [
      'Vše z Růst balíčku',
      'Neomezené stránky',
      'Strategické konzultace',
      'Custom integrace',
      'Dedikovaný manažer',
    ],
    cta: 'Kontaktovat',
    ctaLink: '/audit?package=premium',
    popular: false,
    color: '#0066FF',
  },
];

const Pricing = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-[#0066FF]/10 rounded-full blur-[200px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[350px] h-[350px] bg-[#00D4AA]/10 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#00D4AA]/30 mb-6"
            whileHover={{ scale: 1.03 }}
          >
            <Package className="w-4 h-4 text-[#00D4AA]" />
            <span className="text-sm text-slate-600 font-medium">Ceny</span>
          </motion.div>

          <h2 className="text-headline font-bold mb-6 text-slate-900">
            Vyberte si <span className="text-gradient">balíček</span>
          </h2>

          <p className="text-body text-lg max-w-xl mx-auto">
            Ceny jsou navázány na velikost vašeho webu a množství produktů/služeb.
            Začněte auditem a pokračujte podle potřeb.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group"
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#39FF14] to-[#00D4AA] text-slate-900 text-xs font-bold shadow-lg shadow-[#00D4AA]/30">
                    <Sparkles className="w-3.5 h-3.5" />
                    Doporučeno
                  </div>
                </div>
              )}

              {/* Card */}
              <motion.div
                className={`relative h-full rounded-3xl p-8 transition-all duration-500 ${
                  plan.popular
                    ? 'glass-strong border-[#00D4AA]/30'
                    : 'glass-card'
                }`}
                whileHover={{
                  y: -8,
                  transition: { type: 'spring', stiffness: 400, damping: 20 },
                }}
              >
                {/* Glow Effect */}
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-2xl"
                  style={{
                    background: `radial-gradient(circle at center, ${plan.color}20, transparent 70%)`,
                  }}
                />

                {/* Plan Header */}
                <div className="mb-6">
                  <h3 className="text-title text-slate-800 mb-1">{plan.name}</h3>
                  <p className="text-body text-sm">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-slate-800">{plan.price}</span>
                    <span className="text-slate-500">Kč</span>
                  </div>
                  <span className="text-slate-400 text-sm">{plan.period}</span>
                </div>

                {/* Items Badge */}
                <div
                  className="mb-6 py-2 px-4 rounded-xl"
                  style={{
                    background: `linear-gradient(135deg, ${plan.color}10, ${plan.color}05)`,
                    border: `1px solid ${plan.color}25`,
                  }}
                >
                  <span className="text-sm text-slate-600 font-medium">{plan.items}</span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{
                          background: plan.popular
                            ? `linear-gradient(135deg, ${plan.color}, ${plan.color}80)`
                            : 'rgba(0,0,0,0.05)',
                        }}
                      >
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Link to={plan.ctaLink} className="block">
                  <motion.button
                    className={`w-full py-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#0066FF] to-[#00D4AA] text-white shadow-lg shadow-[#00D4AA]/25'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-slate-400 text-sm mb-4">Potřebujete individuální řešení?</p>
          <Link to="/o-nas">
            <motion.button
              className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-[#00D4AA]/50 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Kontaktujte nás
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
